/**
 * Per-user notification feed, synthesized client-side.
 *
 * There is no notifications table, no `/notifications` endpoint and no socket,
 * and `audit_log` is readable only by qa/admin — so the feed is *derived* from
 * the three list endpoints the app already polls, diffed against a
 * localStorage high-water mark.
 *
 * Two rules make that work:
 *
 * 1. Subscribe to the **canonical** query keys (`['tasks']`,
 *    `['studies','flagged']`, `['ingest','needs-review']`). React Query dedupes
 *    by key, so sharing a key with a page costs zero extra network polls.
 * 2. Gate each query on the permission the endpoint actually requires. A query
 *    the role would 403 on is not "harmless": `App.tsx`'s global
 *    `QueryCache.onError` raises a destructive toast for anything carrying a
 *    request id, so a technician would get an error toast every 30 seconds.
 *
 * Known ceiling: read state is per-browser and there is no history older than
 * current server state. A `notifications` table is the upgrade path.
 */

import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import {
  isAssignedToUser,
  isReviewKind,
  isVisibleKind,
  makeIsSurplusSlot,
  reviewProgress,
  ticketTitle,
} from '@/lib/taskRules'
import type { BadgeProps } from '@/components/ui/badge'
import type { IngestPendingReviewItem, Me, StudyRow, Task } from '@/api/types'

export const SEEN_KEY = 'mi.notifications.seen'

/** Upper bound on the persisted seen-map. Pruning to the live payload already
 *  bounds it; this bounds the payload itself (1000 tasks is a real number). */
export const SEEN_CAP = 500

const POLL_MS = 30_000

export type NotificationKind =
  | 'assigned'
  | 'approved'
  | 'rework'
  | 'available'
  | 'seg_failed'
  | 'escalation'
  | 'disagreement'
  | 'qc_hold'

export type NotificationCategory = 'attention' | 'mine' | 'available'

export interface AppNotification {
  /** Stable across re-polls of unchanged state, distinct on a state change:
   *  `kind:entity:discriminator`. This is what makes the diff idempotent. */
  id: string
  kind: NotificationKind
  category: NotificationCategory
  title: string
  studyUid: string
  /** Deep link to the thing. Only routes the redesign actually defines. */
  to: string
  at: string | null
  /** Amber/red are reserved for human attention — pipeline-owned events stay grey. */
  variant: BadgeProps['variant']
}

export const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  attention: 'Needs your attention',
  mine: 'Your work',
  available: 'Available to claim',
}

export const KIND_LABEL: Record<NotificationKind, string> = {
  assigned: 'Assigned',
  approved: 'Approved',
  rework: 'Rework',
  available: 'Available',
  seg_failed: 'Auto-seg failed',
  escalation: 'Escalated',
  disagreement: 'Disagreement',
  qc_hold: 'QC hold',
}

const CATEGORY_ORDER: NotificationCategory[] = ['attention', 'mine', 'available']

export type SeenMap = Record<string, number>

function readSeen(): SeenMap | null {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as SeenMap) : null
  } catch {
    // Private mode throws on access, and the test harness stubs storage.
    return null
  }
}

function writeSeen(map: SeenMap): void {
  try {
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(map))
  } catch {
    /* nothing to do — the feed still works, it just won't remember */
  }
}

/**
 * Bound the persisted map by dropping the oldest entries past `SEEN_CAP`.
 *
 * Without this the key grows forever — every state change of every task adds a
 * new id. Capping by recency rather than pruning to the current payload is
 * deliberate: the three queries settle at different times, so a payload-based
 * prune run while one is still fetching would evict live ids and re-alert
 * events the user already dismissed.
 */
export function pruneSeen(map: SeenMap): SeenMap {
  const entries = Object.entries(map)
  if (entries.length <= SEEN_CAP) return map
  entries.sort((a, b) => b[1] - a[1])
  return Object.fromEntries(entries.slice(0, SEEN_CAP))
}

function lastDecision(task: Task): string | null {
  const remarks = task.review_remarks
  if (!remarks || remarks.length === 0) return null
  return remarks[remarks.length - 1]?.decision ?? null
}

export interface DeriveInput {
  me: Me | null
  tasks?: Task[]
  studies?: StudyRow[]
  ingest?: IngestPendingReviewItem[]
}

/** Pure — the whole point, so the diff is testable without React or a network. */
export function deriveNotifications({ me, tasks, studies, ingest }: DeriveInput): AppNotification[] {
  if (!me) return []
  const out: AppNotification[] = []
  const mine = (t: Task) => isAssignedToUser(t, me.user_id)
  // Same kind filter the work surface uses, so the bell can't announce a row
  // the user has no way to act on.
  const visible = { role: me.role, canReview: me.permissions.includes('task:review') }
  // …and the same rule the Available tab applies: one slot per step reaches the
  // bell, so a 2-slot node can't announce the same ticket twice.
  const isSurplusSlot = makeIsSurplusSlot(tasks ?? [], me.user_id)

  for (const task of tasks ?? []) {
    if (!isVisibleKind(task, visible)) continue
    const uid = task.study_uid
    const rejections = task.rejection_count ?? 0

    if (mine(task)) {
      // The reviewer's side of a rework round. Their review row SURVIVES every
      // round, so the only thing that tells them the technician is done is
      // `seg_state` flipping back to 'review' — and a radiologist who sent a mask
      // back should not have to sit on the work tab watching for it.
      //
      // `myDecision === null` scopes it to the round they have not judged, which
      // is what makes the id fire once per round rather than every poll.
      if (
        task.task_kind === 'organ_mask_review' &&
        rejections > 0 &&
        task.seg_state === 'review' &&
        task.status !== 'complete' &&
        reviewProgress(task, me.user_id).myDecision === null
      ) {
        out.push({
          id: `reverify:${task.id}:${rejections}`,
          kind: 'rework',
          category: 'attention',
          title: 'Reworked mask is back for your verdict',
          studyUid: uid,
          to: `/work?tab=decide&task=${task.id}`,
          at: task.updated_at,
          variant: 'warning',
        })
        continue
      }
      // A rework request outranks the plain assignment: it's the one that needs
      // the technician to do something differently. Never on a review row —
      // there the rework is somebody else's work, and "your submission" would be
      // announcing the reviewer's own verdict back to them — `isReviewKind`, so an
      // anomaly-read verification is excluded for the same reason a mask's is.
      if (rejections > 0 && !isReviewKind(task) && task.status !== 'complete') {
        out.push({
          id: `rework:${task.id}:${rejections}`,
          kind: 'rework',
          category: 'attention',
          title: 'Rework requested on your submission',
          studyUid: uid,
          to: `/work?tab=progress&task=${task.id}`,
          at: task.updated_at,
          variant: 'warning',
        })
      } else if (task.status === 'complete' && lastDecision(task) !== 'reject') {
        out.push({
          id: `approved:${task.id}:complete`,
          kind: 'approved',
          category: 'mine',
          title: 'Your submission was approved',
          studyUid: uid,
          to: `/activity?task=${task.id}`,
          at: task.updated_at,
          variant: 'outline',
        })
      } else if (task.status === 'assigned' || task.status === 'partially_claimed') {
        out.push({
          id: `assigned:${task.id}:${task.status}`,
          kind: 'assigned',
          category: 'mine',
          // `ticketTitle`, not `task.title`: the stored title is baked at task
          // creation and reads "Review organ mask" on a ticket that has since
          // become the anomaly read.
          title: `${ticketTitle(task, me.user_id)} is assigned to you`,
          studyUid: uid,
          to: `/work?tab=progress&task=${task.id}`,
          at: task.updated_at,
          variant: 'secondary',
        })
      }
      continue
    }

    if (
      (task.status === 'pending' || task.status === 'partially_claimed') &&
      !isSurplusSlot(task)
    ) {
      out.push({
        id: `available:${task.id}:${task.status}`,
        kind: 'available',
        category: 'available',
        title: `${ticketTitle(task, me.user_id)} is available to claim`,
        studyUid: uid,
        to: `/work?tab=available&task=${task.id}`,
        at: task.updated_at,
        variant: 'muted',
      })
    }
  }

  for (const study of studies ?? []) {
    const at = study.updated_at ?? study.started_at
    const target = `/work?tab=decide&study=${encodeURIComponent(study.study_uid)}`
    if (study.seg_state === 'auto_seg_failed') {
      out.push({
        id: `seg_failed:${study.study_uid}:${study.seg_state}`,
        kind: 'seg_failed',
        category: 'attention',
        title: 'Auto-segmentation failed',
        studyUid: study.study_uid,
        to: target,
        at,
        variant: 'warning',
      })
    } else if (study.seg_state === 'awaiting_maintainer') {
      out.push({
        id: `escalation:${study.study_uid}:${study.seg_state}`,
        kind: 'escalation',
        category: 'attention',
        title: 'Mask escalated to a maintainer',
        studyUid: study.study_uid,
        to: target,
        at,
        variant: 'warning',
      })
    }
    if (study.anomaly_state === 'disagreement') {
      out.push({
        id: `disagreement:${study.study_uid}:${study.anomaly_state}`,
        kind: 'disagreement',
        category: 'attention',
        title: 'Radiologists disagree on this read',
        studyUid: study.study_uid,
        to: target,
        at,
        variant: 'warning',
      })
    }
  }

  for (const item of ingest ?? []) {
    out.push({
      // Keyed on the workflow id, not `updated_at`: the row's timestamp moves
      // while the hold is open, which would re-alert the same hold.
      id: `qc_hold:${item.workflow_id}:needs_review`,
      kind: 'qc_hold',
      category: 'attention',
      title: item.reason ? `Ingestion QC hold: ${item.reason}` : 'Ingestion QC needs a decision',
      studyUid: item.study_uid,
      to: `/work?tab=decide&study=${encodeURIComponent(item.study_uid)}`,
      at: item.updated_at,
      variant: 'warning',
    })
  }

  const rank = (n: AppNotification) => CATEGORY_ORDER.indexOf(n.category)
  return out.sort((a, b) => rank(a) - rank(b) || (b.at ?? '').localeCompare(a.at ?? ''))
}

export interface NotificationsState {
  items: AppNotification[]
  unreadCount: number
  isUnread: (id: string) => boolean
  markRead: (id: string) => void
  markAllRead: () => void
}

export function useNotifications(): NotificationsState {
  const { me, can } = useAuth()
  const [seen, setSeen] = useState<SeenMap>(() => readSeen() ?? {})

  const tasks = useQuery<Task[]>({
    queryKey: ['tasks'],
    queryFn: () => apiClient.fetchTasks(),
    refetchInterval: POLL_MS,
    // `developer` has no task:read, so this 403'd every 30 s (four requests with
    // React Query's retries) and raised a red toast each time.
    enabled: !!me && can('task:read'),
  })

  const studies = useQuery({
    queryKey: ['studies', 'flagged'],
    queryFn: () => apiClient.listStudies({ flagged: true }, 100, 0),
    refetchInterval: POLL_MS,
    enabled: !!me && can('study:read_all'),
  })

  const ingest = useQuery<IngestPendingReviewItem[]>({
    queryKey: ['ingest', 'needs-review'],
    queryFn: () => apiClient.listIngestNeedsReview(),
    refetchInterval: POLL_MS,
    enabled: !!me && can('task:review'),
  })

  const items = useMemo(
    () =>
      deriveNotifications({
        me,
        tasks: tasks.data,
        studies: studies.data?.items,
        ingest: ingest.data,
      }),
    [me, tasks.data, studies.data, ingest.data],
  )

  const persist = useCallback((next: SeenMap) => {
    const pruned = pruneSeen(next)
    setSeen(pruned)
    writeSeen(pruned)
  }, [])

  const markRead = useCallback(
    (id: string) => persist({ ...seen, [id]: Date.now() }),
    [seen, persist],
  )

  const markAllRead = useCallback(() => {
    const now = Date.now()
    persist({ ...seen, ...Object.fromEntries(items.map((n) => [n.id, now])) })
  }, [seen, items, persist])

  const isUnread = useCallback((id: string) => !(id in seen), [seen])

  return {
    items,
    unreadCount: items.reduce((n, item) => n + (item.id in seen ? 0 : 1), 0),
    isUnread,
    markRead,
    markAllRead,
  }
}
